import makeStyles from '@mui/styles/makeStyles';
import get from 'lodash/get';
import { useMemo } from 'react';
import MetricChart from './MetricChart';

import { Theme } from '@mui/material/styles/createTheme';
import { CallMetricsResponse, MetricChartProps } from './MetricTypes';

interface MetricsField {
  name: string;
  displayName: string;
  show: boolean;
  chart?: string;
  order?: number;
  titleUnit?: string;
  unit?: string;
}

const useStyles = makeStyles((theme: Theme) => ({
  callMetricsPanelContainerOutline: {
    display: 'flex',
    flexWrap: 'wrap',
    flexDirection: 'row',
    justifyContent: 'space-around',
    alignContent: 'space-between',
    margin: '0px 0px 12px',
    borderBottom:
      theme.palette.mode === 'dark'
        ? 'solid 1px rgb(194,192,195)'
        : '1px solid #ccc',
  },
  callMetricsEmptyText: {
    fontSize: '14px',
  },
  border: {
    borderBottom:
      theme.palette.mode === 'dark'
        ? 'solid 1px rgb(194,192,195)'
        : '1px solid black',
  },
}));

const CallMetricsCharts = (props: {
  callMetrics: CallMetricsResponse | undefined;
  loading: boolean;
}) => {
  const { callMetrics, loading } = props;
  const classes = useStyles();
  const callMetricFields: MetricsField[] = [
    {
      name: 'calls.inbound.answered',
      displayName: 'Answered Inbound Calls',
      show: false,
    },
    {
      name: 'calls.inbound.missed',
      displayName: 'Missed Inbound Calls',
      show: false,
    },
    {
      name: 'calls.outbound',
      displayName: 'Outbound',
      unit: 'Calls',
      chart: 'Calls',
      show: true,
      order: 1,
    },
    {
      name: 'calls.inbound',
      displayName: 'Inbound',
      unit: 'Calls',
      show: true,
      chart: 'Calls',
      order: 1,
    },
    {
      name: 'calls.outbound.answered',
      displayName: 'Answered Outbound Calls',
      show: false,
    },
    {
      name: 'calls.outbound.missed',
      displayName: 'Missed Outbound Calls',
      show: false,
    },
    {
      name: 'callTime.hold',
      displayName: 'Hold Time (min)',
      show: false,
    },
    {
      name: 'callTime.talk',
      displayName: 'Average Talk Time (min)',
      show: false,
    },
    {
      name: 'callTime',
      displayName: 'Total Call Time (min)',
      show: false,
    },
    {
      name: 'callTime.saved',
      displayName: 'Time Saved (min)',
      show: false,
    },
    {
      name: 'callTime.outbound',
      displayName: 'Outbound',
      unit: 'Minutes',
      chart: 'Talk Time',
      order: 0,
      show: true,
    },
    {
      name: 'callTime.inbound',
      displayName: 'Inbound',
      unit: 'Minutes',
      chart: 'Talk Time',
      order: 0,
      show: true,
    },
    {
      name: 'connections.outbound',
      displayName: 'Outbound',
      show: true,
      order: 2,
      titleUnit: 'Connected Calls',
      unit: 'Calls',
      chart: 'Connections',
    },
    {
      name: 'connections.inbound',
      displayName: 'Inbound',
      show: true,
      order: 2,
      titleUnit: 'Connected Calls',
      unit: 'Calls',
      chart: 'Connections',
    },
    {
      name: 'connections',
      displayName: 'Connections per Day',
      show: false,
    },
    {
      name: 'connections',
      displayName: 'Total Connections',
      show: false,
    },
    {
      name: 'sms.sent',
      displayName: 'SMS Sent',
      show: false,
      order: 7,
    },
  ];
  const pad = (num) => {
    return ('0' + num).slice(-2);
  };
  const mmss = (secs) => {
    const minutes = Math.floor(secs / 60);
    secs = Math.floor(secs % 60);
    return `${minutes}:${pad(secs)}`;
  };
  const metricsDisplayArray = useMemo(() => {
    const callMetricFieldsDisplayArray = callMetricFields
      .filter(
        (metricField: MetricsField) =>
          metricField?.show && metricField?.order !== undefined
      )
      .sort(
        (a: MetricsField, b: MetricsField) => (a?.order ?? 0) - (b?.order ?? 0)
      );
    const chartData: any = {};
    callMetricFieldsDisplayArray.forEach((metricField: MetricsField) => {
      if (metricField.chart) {
        if (!chartData[metricField.chart]) {
          chartData[metricField.chart] = [];
        }
        const metricData = get(callMetrics, metricField.name, null);
        if (metricData) {
          const metricToDisplay = {
            title: metricField.displayName,
            data: metricData.total,
            // we want to display it in minutes but it is given to us in seconds
            displayData:
              metricField.unit === 'Minutes'
                ? mmss(metricData.total)
                : metricData.total,
            loading,
            chart: metricField.chart,
            unit: metricField.unit,
            order: metricField.order,
            titleUnit: metricField?.titleUnit,
          };
          chartData[metricField.chart].push(metricToDisplay);
        }
      }
    });
    const chartDataArray: {
      [keyTitle: string]: MetricChartProps[];
    }[] = [];
    for (const key in chartData) {
      const chartDataObject: {
        [keyTitle: string]: MetricChartProps[];
      } = {};
      chartDataObject[key] = chartData[key];
      chartDataArray.push(chartDataObject);
    }
    return chartDataArray;
  }, [callMetrics, loading]);

  const chartsToDisplay: string[] = useMemo(() => {
    return metricsDisplayArray
      .map(
        (
          elem: {
            [keyTitle: string]: MetricChartProps[];
          },
          index: number
        ) => {
          return Object.keys(elem)?.[0] ?? '';
        }
      )
      .filter((stringName: string) => stringName);
  }, [metricsDisplayArray]);

  if (callMetrics === undefined) {
    return <div className={classes.border}></div>;
  }

  return (
    <div className={classes.callMetricsPanelContainerOutline}>
      {metricsDisplayArray?.length
        ? metricsDisplayArray.map(
            (
              metricToDisplay: {
                [keyTitle: string]: MetricChartProps[];
              },
              index: number
            ) => {
              if (!metricToDisplay?.[chartsToDisplay[index]]?.length) {
                return <></>;
              }
              return (
                <MetricChart
                  title={chartsToDisplay[index]}
                  key={index}
                  data={metricToDisplay}
                  loading={loading}
                />
              );
            }
          )
        : null}
    </div>
  );
};
export default CallMetricsCharts;
